import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import { PageBar, Content } from '../components/Layout'
import { Badge, Empty, ErrorBox, KV, Loading, Panel } from '../components/ui'
import { api } from '../lib/api'
import { gbp, longDate, shortDate, timeOnly } from '../lib/format'
import type { ClientDetail as Detail } from '../types'

export function ClientDetail() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, error } = useQuery({
    queryKey: ['client', id], queryFn: () => api.get<Detail>(`/clients/${id}`),
  })

  if (isLoading) return <><PageBar title="Client" /><Content><Loading /></Content></>
  if (error || !data) return <><PageBar title="Client" /><Content><ErrorBox error={error} /></Content></>

  const { client, bookings, invoices } = data

  return (
    <>
      <PageBar
        title={client.name || client.email}
        actions={
          <>
            <Link className="btn ghost" to="/clients">All clients</Link>
            <Link className="btn" to="/bookings/new">New booking</Link>
          </>
        }
      />
      <Content>
        <Panel pad>
          <KV label="Email">{client.email}</KV>
          {client.phone && <KV label="Phone">{client.phone}</KV>}
          <KV label="Segment"><Badge kind="seg" value={client.segment} /></KV>
          <KV label="Client since">{longDate(client.created_at)}</KV>
        </Panel>

        <h2 className="sec">Bookings</h2>
        <Panel>
          {bookings.length ? (
            <table><tbody>
              {bookings.map((b) => (
                <tr key={b.id}>
                  <td className="when">
                    <Link to={`/bookings/${b.id}`}>{shortDate(b.starts_at)}, {timeOnly(b.starts_at)}</Link>
                  </td>
                  <td>{b.service_code}</td>
                  <td>{gbp(b.quoted_pence)}</td>
                  <td><Badge kind="b" value={b.status} /></td>
                </tr>
              ))}
            </tbody></table>
          ) : <Empty>No bookings yet.</Empty>}
        </Panel>

        <h2 className="sec">Invoices</h2>
        <Panel>
          {invoices.length ? (
            <table><tbody>
              {invoices.map((i) => (
                <tr key={i.id}>
                  <td><Link to={`/invoices/${i.id}`}><b>{i.number}</b></Link></td>
                  <td className="snippet">{i.description}</td>
                  <td>{gbp(i.gross_pence)}</td>
                  <td><Badge kind="inv" value={i.status} /></td>
                </tr>
              ))}
            </tbody></table>
          ) : <Empty>No invoices yet.</Empty>}
        </Panel>
      </Content>
    </>
  )
}
